import { BaseApi, HttpMethodEnum } from '../BaseApi/BaseApi';
import { S3UploadPolicy } from '../Documents/DocumentApi.ts';
import {
    IEICFEscalationConfig,
    IEICFFacilityAssignmentRequest,
    IEICFTemplate,
    IEICFTemplateInput,
} from '../../entities/EICFTemplates/EICFTemplate.ts';

export class EICFTemplatesApi extends BaseApi {
    private static baseApiUrl: string = `${BaseApi.apiUrl}/EICFTemplates`;

    public static apiUrls = {
        getByFacility: `${EICFTemplatesApi.baseApiUrl}`,
        documentUploadUrl: `${EICFTemplatesApi.baseApiUrl}/document-upload-url`,
        getById: `${EICFTemplatesApi.baseApiUrl}/:id`,
        getActiveForFacility: `${EICFTemplatesApi.baseApiUrl}/facility/:id/active`,
        post: `${EICFTemplatesApi.baseApiUrl}`,
        put: `${EICFTemplatesApi.baseApiUrl}/:id`,
        delete: `${EICFTemplatesApi.baseApiUrl}/:id`,
        approve: `${EICFTemplatesApi.baseApiUrl}/:id/approve`,
        assignToFacility: `${EICFTemplatesApi.baseApiUrl}/assign-to-facility`,
        unassignFromFacility: `${EICFTemplatesApi.baseApiUrl}/facility/:id/assignment`,
        getAllowedSigners: `${EICFTemplatesApi.baseApiUrl}/facility/:id/allowed-signers`,
        setAllowedSigners: `${EICFTemplatesApi.baseApiUrl}/facility/:id/allowed-signers`,
        getEscalationConfig: `${EICFTemplatesApi.baseApiUrl}/facility/:id/escalation-config`,
        setEscalationConfig: `${EICFTemplatesApi.baseApiUrl}/facility/:id/escalation-config`,
    };

    public static async getByFacility(facilityId: number): Promise<IEICFTemplate[]> {
        return this.sendApiRequest<IEICFTemplate[]>({
            method: HttpMethodEnum.GET,
            path: `${this.apiUrls.getByFacility}?facilityId=${facilityId}`,
            data: undefined,
        });
    }

    public static async getById(id: number): Promise<IEICFTemplate> {
        return this.sendApiRequest<IEICFTemplate>({
            method: HttpMethodEnum.GET,
            path: this.apiUrls.getById,
            data: undefined,
            id: id.toString(),
        });
    }

    public static async getActiveForFacility(facilityId: number): Promise<IEICFTemplate | null> {
        return this.sendApiRequest<IEICFTemplate | null>({
            method: HttpMethodEnum.GET,
            path: this.apiUrls.getActiveForFacility,
            data: undefined,
            id: facilityId.toString(),
        });
    }

    public static async getDocumentUploadUrl(
        facilityId: number,
        filename: string,
    ): Promise<S3UploadPolicy> {
        return this.sendApiRequest<S3UploadPolicy>({
            method: HttpMethodEnum.GET,
            path: `${this.apiUrls.documentUploadUrl}?facilityId=${facilityId}&filename=${filename}`,
        });
    }

    public static async post(data: IEICFTemplateInput): Promise<IEICFTemplate> {
        return this.sendApiRequest<IEICFTemplate>({
            method: HttpMethodEnum.POST,
            path: this.apiUrls.post,
            data,
        });
    }

    public static async put(id: number, data: IEICFTemplateInput): Promise<IEICFTemplate> {
        return this.sendApiRequest<IEICFTemplate>({
            method: HttpMethodEnum.PUT,
            path: this.apiUrls.put,
            data,
            id: id.toString(),
        });
    }

    public static async delete(id: number): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.DELETE,
            path: this.apiUrls.delete,
            data: undefined,
            id: id.toString(),
        });
    }

    public static async approve(id: number): Promise<IEICFTemplate> {
        return this.sendApiRequest<IEICFTemplate>({
            method: HttpMethodEnum.POST,
            path: this.apiUrls.approve,
            data: undefined,
            id: id.toString(),
        });
    }

    public static async assignToFacility(data: IEICFFacilityAssignmentRequest): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.POST,
            path: this.apiUrls.assignToFacility,
            data,
        });
    }

    public static async unassignFromFacility(facilityId: number): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.DELETE,
            path: this.apiUrls.unassignFromFacility,
            data: undefined,
            id: facilityId.toString(),
        });
    }

    public static async getAllowedSigners(facilityId: number): Promise<number[]> {
        return this.sendApiRequest<number[]>({
            method: HttpMethodEnum.GET,
            path: this.apiUrls.getAllowedSigners,
            data: undefined,
            id: facilityId.toString(),
        });
    }

    public static async setAllowedSigners(facilityId: number, hcpIds: number[]): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.PUT,
            path: this.apiUrls.setAllowedSigners,
            data: { hcpIds },
            id: facilityId.toString(),
        });
    }

    public static async getEscalationConfig(
        facilityId: number,
    ): Promise<IEICFEscalationConfig> {
        return this.sendApiRequest<IEICFEscalationConfig>({
            method: HttpMethodEnum.GET,
            path: this.apiUrls.getEscalationConfig,
            data: undefined,
            id: facilityId.toString(),
        });
    }

    public static async setEscalationConfig(
        facilityId: number,
        config: IEICFEscalationConfig,
    ): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.PUT,
            path: this.apiUrls.setEscalationConfig,
            data: config,
            id: facilityId.toString(),
        });
    }
}
